import { HttpErrorResponse } from '@angular/common/http';
import { ITagDTO, ITagUsageDTO } from '@modeso/types__aumico-lib-companies-be';
import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import * as fromTagsActions from '../actions/tags.actions';

/**
 * REV-22155 — store of the tags: all tags of the organization (sorted by name, as the backend
 * delivers them), the usage of the tag chosen for deletion, and the last error.
 */
export const tagsFeatureKey = 'aumicoTags';

export interface TagsFeatureState {
    tags: ITagDTO[];
    /** Usage of the tag whose "x" was clicked; null when no deletion is pending. */
    tagUsage: { id: string; usage: ITagUsageDTO } | null;
    error: HttpErrorResponse | null;
}

export const initialTagsState: TagsFeatureState = {
    tags: [],
    tagUsage: null,
    error: null,
};

export const selectTagsFeature = createFeatureSelector<TagsFeatureState>(tagsFeatureKey);
export const selectAllTags = createSelector(selectTagsFeature, (state) => state.tags);
export const selectTagUsage = createSelector(selectTagsFeature, (state) => state.tagUsage);
export const selectTagsError = createSelector(selectTagsFeature, (state) => state.error);
/** Tag id → name, so pages resolve the tag ids of persons/companies without searching the list. */
export const selectTagNamesById = createSelector(selectAllTags, (tags) =>
    tags.reduce<Record<string, string>>((names, tag) => ({ ...names, [tag._id]: tag.name }), {}),
);

const byName = (a: ITagDTO, b: ITagDTO) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
const clearError = (state: TagsFeatureState): TagsFeatureState => ({ ...state, error: null });
const setError = (state: TagsFeatureState, { payload }: { payload: HttpErrorResponse }): TagsFeatureState => ({
    ...state,
    error: payload,
});

export const tagsReducer = createReducer(
    initialTagsState,

    on(fromTagsActions.getTags, clearError),
    on(fromTagsActions.onGettingTagsSuccessfully, (state, { payload }) => ({ ...state, tags: payload, error: null })),
    on(fromTagsActions.onGettingTagsFailed, setError),

    on(fromTagsActions.createTag, clearError),
    on(fromTagsActions.onCreateTagSuccessfully, (state, { payload }) => ({
        ...state,
        tags: [...state.tags, payload].sort(byName),
    })),
    on(fromTagsActions.onCreateTagFailed, setError),

    on(fromTagsActions.getTagUsage, (state) => ({ ...state, tagUsage: null, error: null })),
    on(fromTagsActions.onGettingTagUsageSuccessfully, (state, { id, payload }) => ({
        ...state,
        tagUsage: { id, usage: payload },
    })),
    on(fromTagsActions.onGettingTagUsageFailed, setError),
    on(fromTagsActions.clearTagUsage, (state) => ({ ...state, tagUsage: null })),

    on(fromTagsActions.deleteTag, clearError),
    on(fromTagsActions.onDeleteTagSuccessfully, (state, { id }) => ({
        ...state,
        tags: state.tags.filter((tag) => tag._id !== id),
        tagUsage: null,
    })),
    on(fromTagsActions.onDeleteTagFailed, setError),

    on(fromTagsActions.resetTagsErrors, clearError),
    on(fromTagsActions.resetTags, () => initialTagsState),
);

export function tagsReducerFn(state: TagsFeatureState | undefined, action: Action) {
    return tagsReducer(state, action);
}
